/* eslint-disable import/prefer-default-export */
import React, {
  useMemo,
  useState,
} from 'react';
import Button from 'pages/Dashboard/components/Button';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Popover from 'core/components/Popover';
import FilterComponents from 'core/Filters/FilterComponents';
import {
  useFormContext,
} from 'react-hook-form';
import {
  useMixpanelTrack,
  useNotify,
  useSelectableContext,
} from 'core/hooks';
import {
  useTriggerReport,
} from 'pages/Dashboard/pages/Reports/hooks';
import {
  reportEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  ReportSettingsDTO,
} from 'dtos/reportSettingsDTO';
import type {
  ReportForm,
} from 'pages/Dashboard/pages/Reports/ReportDialog';
import {
  VirtualTableColumnDef,
} from 'core/components/VirtualTable/types';
import {
  normalizeFilterableSettings,
  serializeFilterQuery,
} from 'core/Filters/util';
import Tag from 'core/components/Tag';
import isNil from 'lodash/isNil';

type ReportActionCellProps = {
  original: ReportSettingsDTO;
};

function ReportActionCell({ original }: ReportActionCellProps) {
  const hasParameters = useMemo(() => (
    normalizeFilterableSettings(original?.parameters ?? []).length > 0
  ), [original]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const key = String(original?.reportName ?? original?.endpointName ?? '');
  const { isSelected, add, remove } = useSelectableContext();
  const isRunning = isSelected(key);

  const mixPanelTrack = useMixpanelTrack();
  const notify = useNotify();
  const { mutateAsync: triggerReport } = useTriggerReport();
  const { watch, formState: { isValid } } = useFormContext<ReportForm>();
  const values = watch();
  const filterComponents = useMemo(() => normalizeFilterableSettings(
    original?.parameters ?? [],
  ), [original]);

  const components = useMemo(() => (
    filterComponents.map(({ name, ...component }) => ({
      ...component,
      name: `filter.${name}`,
    }))
  ), [filterComponents]);

  const handleReportAction = async (withFilters: boolean) => {
    add(key);
    try {
      const payloadFilter = withFilters
        ? serializeFilterQuery((values?.filter ?? {}), filterComponents)
        : {};
      await triggerReport({
        ...(withFilters ? { filter: payloadFilter } : { filter: {} }),
        endpointName: original?.endpointName ?? '',
        reportName: original?.reportName,
      });
      mixPanelTrack('runReport', { reportName: original?.reportName });
      notify({ variant: 'success', message: 'Reports will be available for download when ready' });
    } finally {
      reportEventChannel.emit('onReportChange');
      remove(key);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        type="button"
        disableElevation
        disabled={isRunning}
        className="!px-2 !h-6 !rounded-full !normal-case flex items-center gap-1 whitespace-nowrap"
        aria-haspopup={hasParameters ? 'dialog' : undefined}
        aria-expanded={Boolean(anchorEl)}
        aria-label={`Run report ${original?.reportDisplayName ?? ''}`}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (hasParameters) {
            setAnchorEl(e.currentTarget);
          } else {
            await handleReportAction(false);
          }
        }}
      >
        {!isRunning && (
          <PlayArrowIcon className="!w-5 !h-5" />
        )}
        {isRunning ? 'In Progress' : 'Run'}
      </Button>

      { !isNil(anchorEl) && (
        <Popover
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: '!p-4 !rounded-xl !shadow-2xl !border !border-gray-200' }}
        >
          <div className="flex flex-col gap-3 w-[24rem]">
            {components.length > 0 && (
            <FilterComponents
              components={components}
              classes={{
                datePicker: {
                  root: '!flex-1',
                  inputContainer: '!w-full',
                },
              }}
            />
            )}
            <div className="flex flex-row justify-between gap-4">
              <Button
                variant="outlined"
                type="button"
                className="w-full min-w-full !max-w-full"
                onClick={() => setAnchorEl(null)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={isRunning || !isValid}
                className="w-full min-w-full !max-w-full"
                onClick={async () => {
                  setAnchorEl(null);
                  await handleReportAction(true);
                }}
              >
                {isRunning ? 'In Progress' : 'Run'}
              </Button>
            </div>
          </div>
        </Popover>
      )}
    </>
  );
}

export const columns: VirtualTableColumnDef<ReportSettingsDTO>[] = [
  {
    id: 'report',
    header: 'Report',
    size: 600,
    minSize: 300,
    cell: ({ row: { original: { reportDisplayName, reportDescription } } }) => (
      <div className="flex flex-col break-words whitespace-normal">
        <span className="font-semibold">{reportDisplayName}</span>
        <span className="!text-[10px] text-gray-500">{reportDescription ?? ''}</span>
      </div>
    ),
  },
  {
    id: 'category',
    header: 'Category',
    size: 120,
    cell: ({ row: { original: { category } } }) => (
      <Tag
        tagName={category ?? '--'}
        className="truncate"
        defaultColor="bg-gray-200"
      />
    ),
  },
  {
    id: 'action',
    header: '',
    size: 100,
    cell: ({ row: { original } }) => (
      <ReportActionCell original={original} />
    ),
  },
];
